import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import { getExtensionPointLabel } from 'constants/ExtensionPointEnum';
import FailedCallbacksTable from './FailedCallbacksTable';
import WebhookRegistrationForm from './WebhookRegistrationForm';
import DisableConfirmDialog from './DisableConfirmDialog';
import useUpdateWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useUpdateWebhookRegistration';
import useDisableWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useDisableWebhookRegistration';
import useEnableWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useEnableWebhookRegistration';
import useGetDetailWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useGetDetailWebhookRegistration';

const LabelValue = ({ label, value }) => (
  <Box mb={1}>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2'>{value ?? '-'}</Typography>
  </Box>
);

const WebhookRegistrationDetail = ({ open, item, onClose }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);

  const updateMutate = useUpdateWebhookRegistration();
  const disableMutate = useDisableWebhookRegistration();
  const enableMutate = useEnableWebhookRegistration();

  const { data: detailResponse, isLoading: isDetailLoading } = useGetDetailWebhookRegistration(
    open ? item?.Id : null,
  );

  if (!item) return null;

  // Prefer freshly fetched detail; fall back to the list-row data while loading
  const detail = detailResponse?.data ?? item;

  const handleUpdate = (data) => {
    updateMutate.mutate(data, {
      onSuccess: () => setEditOpen(false),
    });
  };

  const handleDisableConfirm = () => {
    disableMutate.mutate(detail.Id, {
      onSuccess: () => setDisableConfirmOpen(false),
    });
  };

  const handleEnable = () => {
    enableMutate.mutate(detail.Id);
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3 } }}
      >
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h6'>Webhook Registration Detail</Typography>
          <Button size='small' onClick={onClose}>
            Close
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isDetailLoading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <LabelValue label='Endpoint URL' value={detail.EndpointUrl} />
            <LabelValue label='Event' value={getExtensionPointLabel(detail.ExtensionPointId)} />
            <LabelValue
              label='App'
              value={detail.IsAppSpecific ? (detail.AppName ?? '-') : 'All Apps'}
            />
            <LabelValue label='HTTP Method' value={detail.HttpMethod} />
            <LabelValue label='Request Headers' value={detail.RequestHeaders} />
            <Box mb={1}>
              <Typography variant='caption' color='text.secondary'>
                Status
              </Typography>
              <Box>
                <Chip
                  label={detail.IsActive ? 'Active' : 'Disabled'}
                  color={detail.IsActive ? 'success' : 'default'}
                  size='small'
                />
              </Box>
            </Box>
            <LabelValue
              label='Created At'
              value={detail.CreatedAt ? new Date(detail.CreatedAt).toLocaleString() : '-'}
            />

            <Stack direction='row' spacing={1} mt={2}>
              <Button variant='outlined' size='small' onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              {detail.IsActive && (
                <Button
                  variant='outlined'
                  color='warning'
                  size='small'
                  onClick={() => setDisableConfirmOpen(true)}
                >
                  Disable
                </Button>
              )}
              {!detail.IsActive && (
                <Button
                  variant='outlined'
                  color='success'
                  size='small'
                  disabled={enableMutate.isPending}
                  onClick={handleEnable}
                >
                  Re-enable
                </Button>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <FailedCallbacksTable hookId={detail.Id} />
          </>
        )}
      </Drawer>

      <WebhookRegistrationForm
        visible={editOpen}
        isEdit
        loading={updateMutate.isPending}
        handleClose={() => setEditOpen(false)}
        handleSubmit={handleUpdate}
        initialValues={detail}
      />

      <DisableConfirmDialog
        open={disableConfirmOpen}
        loading={disableMutate.isPending}
        onConfirm={handleDisableConfirm}
        onCancel={() => setDisableConfirmOpen(false)}
      />
    </>
  );
};

export default React.memo(WebhookRegistrationDetail);
